/** Display only; nothing here derives a metric. Null renders as an em dash, never zero. */

export const DASH = "—";

export const formatCurrency = (v: number, compact = false): string => {
    const sign = v < 0 ? "-" : "";
    const abs = Math.abs(v);
    if (compact) {
        if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
        if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
        if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    }
    return `${sign}$${abs.toFixed(2)}`;
};

export const formatNumber = (v: number, compact = false): string => {
    if (compact) {
        const abs = Math.abs(v);
        // Without these thresholds 11.9 million renders as "11890.0k".
        if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
        if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
        if (abs >= 10_000) return `${(v / 1_000).toFixed(1)}k`;
    }
    return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
};

/**
 * Precision follows magnitude. Conversion rates here run near 0.004%, which one decimal rounds to
 * a flat zero — the figure reads as "nothing happened" when it is the whole point of the column.
 */
export const formatPercent = (v: number, digits?: number): string => {
    const pct = v * 100;
    if (digits !== undefined) return `${pct.toFixed(digits)}%`;
    if (pct === 0) return "0%";
    if (Math.abs(pct) >= 0.1) return `${pct.toFixed(1)}%`;
    return `${pct.toFixed(3)}%`;
};

/** `unit` comes from the backend catalogue, so a metric always formats the same way everywhere. */
export const formatMetric = (
    v: number | null | undefined,
    unit: string,
    compact = false
): string => {
    if (v === null || v === undefined) return DASH;
    if (!Number.isFinite(v)) return "∞";
    if (unit === "currency") return formatCurrency(v, compact);
    if (unit === "percent") return formatPercent(v);
    if (unit === "multiple") return `${v.toFixed(2)}\u00d7`;
    return formatNumber(v, compact);
};

/** Signed form for deltas and contributions, where the direction is the point. */
export const formatSigned = (v: number | null | undefined, unit: string, compact = false): string => {
    if (v === null || v === undefined) return DASH;
    if (!Number.isFinite(v)) return "∞";
    const prefix = v > 0 ? "+" : "";
    return `${prefix}${formatMetric(v, unit, compact)}`;
};

/** One rule for signed figures everywhere: below zero is red, above zero is green, zero is neither. */
export const signColor = (v: number | null | undefined): string => {
    if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return "text.primary";
    return v < 0 ? "error.main" : "success.main";
};

/** The same rule as a raw hex, for SVG fills and strokes that cannot take a theme path. */
export const signHex = (v: number | null | undefined, neutral: string): string => {
    if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return neutral;
    return v < 0 ? "#e11d48" : "#059669";
};

/** Colour for a figure that cannot go negative — CPM, cost, RPM, impressions. */
export const directionColor = (accent: string | null | undefined): string => {
    if (accent === "negative") return "warning.main";
    if (accent === "positive") return "success.main";
    return "text.primary";
};

/** Shrink the headline as the string grows, so a long figure fits rather than spilling its card. */
export const valueFontSize = (text: string): number => {
    const n = text.length;
    if (n <= 6) return 22;
    if (n <= 8) return 19;
    if (n <= 10) return 17;
    return 15;
};

/** "hour" / "3 hours" etc, so a label names the unit it is actually counting. */
export const unitLabel = (n: number, unit: string): string =>
    n === 1 ? unit : `${n} ${unit}s`;

/** Hour buckets arrive as "YYYY-MM-DD HH:00"; this is the instant they start at. */
export const bucketToInstant = (bucket: string): string =>
    bucket.length > 10
        ? `${bucket.slice(0, 10)}T${bucket.slice(11, 16)}:00Z`
        : `${bucket}T00:00:00Z`;

export const addHoursIso = (iso: string, hours: number): string =>
    new Date(Date.parse(iso) + hours * 3_600_000).toISOString();

export const hoursBetweenIso = (from: string, to: string): number =>
    Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 3_600_000));

export const todayUtc = (): string => new Date().toISOString().slice(0, 10);

export const addDaysUtc = (ymd: string, days: number): string => {
    const d = new Date(`${ymd}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

export const daysBetweenUtc = (from: string, to: string): number =>
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;

/** The window immediately before the selection, of the same length. */
export const previousWindow = (from: string, to: string): { baseFrom: string; baseTo: string } => {
    const len = daysBetweenUtc(from, to);
    return { baseFrom: addDaysUtc(from, -len), baseTo: addDaysUtc(from, -1) };
};
